/**
 * Zone geometry: does a route go somewhere it was told not to?
 *
 * Zones are advisory on this system — the aircraft carries no fence — so the
 * deterrence that exists has to happen before the mission is uploaded. That
 * makes this the load-bearing part: a keep-out only means anything if a route
 * that crosses it is refused at the point an operator tries to send it.
 *
 * Testing a waypoint alone is not enough. A leg between two waypoints that both
 * sit outside a zone can still fly straight through the middle of it, which is
 * the failure that matters most and the one a naive check misses. Legs are
 * therefore sampled along their length rather than at their ends.
 */

import type { Zone, ZoneCircle } from '@/stores/missionStore';
import type { Waypoint } from '@/types/domain';

const EARTH_RADIUS_M = 6_371_000;

/** Great-circle distance. The same maths the planning panel measures legs with. */
export function distanceM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad;
  const dLon = (lon2 - lon1) * toRad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function pointInCircle(lat: number, lon: number, circle: ZoneCircle): boolean {
  return distanceM(lat, lon, circle.lat, circle.lon) <= circle.radiusM;
}

/**
 * Ray casting in lat/lon space.
 *
 * Longitude is not corrected for latitude here, and does not need to be: the
 * test is a parity count of edge crossings, which is invariant under the
 * uniform horizontal scale that correction would apply. It does break across
 * the antimeridian, which no AOR in this system spans.
 */
export function pointInPolygon(
  lat: number,
  lon: number,
  points: ReadonlyArray<{ lat: number; lon: number }>,
): boolean {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i, i += 1) {
    const a = points[i]!;
    const b = points[j]!;
    const straddles = a.lon > lon !== b.lon > lon;
    if (!straddles) continue;
    const crossLat = ((b.lat - a.lat) * (lon - a.lon)) / (b.lon - a.lon) + a.lat;
    if (lat < crossLat) inside = !inside;
  }
  return inside;
}

export function pointInZone(lat: number, lon: number, zone: Zone): boolean {
  return zone.shape.type === 'circle'
    ? pointInCircle(lat, lon, zone.shape)
    : pointInPolygon(lat, lon, zone.shape.points);
}

/** How finely a leg is sampled when asking whether it clips a zone. */
export const LEG_SAMPLE_M = 50;
/** A sample cap, so a 400 km ferry leg cannot lock the panel up. */
const MAX_SAMPLES = 400;

/**
 * Every point on a leg, sampled. Endpoints included, so a route whose waypoint
 * sits inside a zone is caught even when the leg is shorter than one step.
 */
function sampleLeg(
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): Array<{ lat: number; lon: number }> {
  const span = distanceM(aLat, aLon, bLat, bLon);
  const steps = Math.min(MAX_SAMPLES, Math.max(1, Math.ceil(span / LEG_SAMPLE_M)));
  const out: Array<{ lat: number; lon: number }> = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    out.push({ lat: aLat + (bLat - aLat) * t, lon: aLon + (bLon - aLon) * t });
  }
  return out;
}

export interface ZoneViolation {
  zoneId: string;
  zoneName: string;
  kind: 'keep-in' | 'keep-out';
  /** Index of the waypoint, or of the leg's first waypoint when it is a leg. */
  index: number;
  /** True when the breach is mid-leg rather than at a waypoint. */
  onLeg: boolean;
}

function isPositional(waypoint: Waypoint): boolean {
  return !(waypoint.lat === 0 && waypoint.lon === 0);
}

/**
 * Which zones a route violates.
 *
 * A keep-out is violated by being inside it. A keep-in is violated by being
 * outside it — and only when the route has points at all, because an empty
 * route has not left anywhere.
 */
export function routeViolations(waypoints: readonly Waypoint[], zones: readonly Zone[]): ZoneViolation[] {
  const positional = waypoints.filter(isPositional);
  if (positional.length === 0 || zones.length === 0) return [];

  return zones
    .map((zone) => firstViolation(positional, zone))
    .filter((v): v is ZoneViolation => v !== null);
}

/** The first place a route breaches one zone, or null if it never does. */
function firstViolation(positional: readonly Waypoint[], zone: Zone): ZoneViolation | null {
  const breaches = (lat: number, lon: number): boolean =>
    zone.kind === 'keep-out' ? pointInZone(lat, lon, zone) : !pointInZone(lat, lon, zone);

  for (let i = 0; i < positional.length; i += 1) {
    const wp = positional[i]!;
    if (breaches(wp.lat, wp.lon)) {
      return { zoneId: zone.zoneId, zoneName: zone.name, kind: zone.kind, index: i, onLeg: false };
    }
    const next = positional[i + 1];
    if (!next) continue;
    // The leg itself, not just its ends: a straight line between two clean
    // waypoints can still pass through the middle of a zone.
    for (const point of sampleLeg(wp.lat, wp.lon, next.lat, next.lon)) {
      if (breaches(point.lat, point.lon)) {
        return { zoneId: zone.zoneId, zoneName: zone.name, kind: zone.kind, index: i, onLeg: true };
      }
    }
  }
  return null;
}

/** A ring of lat/lon points approximating a circle, for drawing and for rects. */
export function rectanglePoints(
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): Array<{ lat: number; lon: number }> {
  return [
    { lat: aLat, lon: aLon },
    { lat: aLat, lon: bLon },
    { lat: bLat, lon: bLon },
    { lat: bLat, lon: aLon },
  ];
}
